import { Controller } from "@hotwired/stimulus";
import { connectPath } from "../base_path";

// Signed-in visitor's account management (SUR-2174). Each contact field lives in
// a `[data-field]` block with `[data-role=...]` elements; changing a value is a
// verified two-step (send a code to the NEW value → enter it to save). Delete is
// a soft-delete behind a two-tap inline confirm. All requests are same-origin
// and carry the session cookie.
export default class extends Controller {
  // Name is a plain profile field — saved directly (no verification code).
  async saveName(event) {
    const block = event.target.closest("[data-field]");
    const value = this.role(block, "value").value.trim();
    this.clearError(block);
    this.hideOk(block);
    if (!value) return this.error(block, "Enter your name.");

    this.busy(event.target, true);
    try {
      const res = await this.post("/account/profile", { name: value });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return this.error(block, data.error || "Couldn't save your name.");
      this.role(block, "current").textContent = data.name || value;
      const ok = this.role(block, "ok");
      if (ok) ok.hidden = false;
    } catch (_e) {
      this.error(block, "Something went wrong — please try again.");
    } finally {
      this.busy(event.target, false);
    }
  }

  // Step 1: send a verification code to the new value.
  async start(event) {
    const block = event.target.closest("[data-field]");
    const field = block.dataset.field;
    const value = this.role(block, "value").value.trim();
    this.clearError(block);
    if (!value) return this.error(block, "Enter a new value first.");

    this.busy(event.target, true);
    try {
      const res = await this.post("/account/change", { field, value });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return this.error(block, data.error || "Couldn't send a code.");
      this.role(block, "codeRow").hidden = false;
      this.role(block, "code").focus();
    } catch (_e) {
      this.error(block, "Something went wrong — please try again.");
    } finally {
      this.busy(event.target, false);
    }
  }

  // Step 2: verify the code and apply the change.
  async verify(event) {
    const block = event.target.closest("[data-field]");
    const field = block.dataset.field;
    const value = this.role(block, "value").value.trim();
    const code = this.role(block, "code").value.trim();
    this.clearError(block);
    if (!/^\d{6}$/.test(code)) return this.error(block, "Enter the 6-digit code.");

    this.busy(event.target, true);
    try {
      const res = await this.post("/account/change/verify", { field, value, code });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return this.error(block, data.error || "That code is invalid or expired.");
      this.role(block, "current").textContent = value;
      this.role(block, "codeRow").hidden = true;
      this.role(block, "value").value = "";
      const ok = this.role(block, "ok");
      if (ok) ok.hidden = false;
    } catch (_e) {
      this.error(block, "Something went wrong — please try again.");
    } finally {
      this.busy(event.target, false);
    }
  }

  async signOut(event) {
    if (event) event.preventDefault();
    try {
      await this.post("/account/sign_out", {});
    } catch (_e) { /* redirect regardless */ }
    window.location.assign(connectPath("/"));
  }

  // Remove a saved search from its dashboard card — two-tap inline confirm
  // (same pattern as deleteAccount), then DELETE and drop the card. The card
  // root carries data-saved-search-id; the buttons live in its links row.
  async deleteSavedSearch(event) {
    const card = event.target.closest("[data-saved-search-id]");
    if (!card) return;
    const btn = card.querySelector("[data-role=searchDeleteBtn]");
    const confirmBtn = card.querySelector("[data-role=searchDeleteConfirm]");
    if (event.target === btn) {
      btn.hidden = true;
      if (confirmBtn) confirmBtn.hidden = false;
      return;
    }
    this.busy(confirmBtn, true);
    try {
      const res = await fetch(`/account/saved_searches/${card.dataset.savedSearchId}`, {
        method: "DELETE", headers: { Accept: "application/json" }, credentials: "same-origin",
      });
      if (!res.ok) throw new Error("delete failed");
      card.remove();
    } catch (_e) {
      this.busy(confirmBtn, false);
      if (confirmBtn) confirmBtn.hidden = true;
      if (btn) btn.hidden = false;
    }
  }

  // Two-tap inline confirm, then soft-delete.
  async deleteAccount(event) {
    const btn = this.role(this.element, "deleteBtn");
    const confirmBtn = this.role(this.element, "deleteConfirm");
    if (event.target === btn) {
      btn.hidden = true;
      if (confirmBtn) confirmBtn.hidden = false;
      return;
    }
    try {
      const res = await fetch("/account", { method: "DELETE", headers: { Accept: "application/json" }, credentials: "same-origin" });
      const data = await res.json().catch(() => ({}));
      window.location.assign(connectPath(data.redirect || "/"));
    } catch (_e) {
      window.location.assign(connectPath("/"));
    }
  }

  post(url, fields) {
    const body = new FormData();
    Object.entries(fields).forEach(([k, v]) => body.append(k, v));
    return fetch(url, { method: "POST", headers: { Accept: "application/json" }, body, credentials: "same-origin" });
  }

  role(scope, name) {
    return scope.querySelector(`[data-role=${name}]`);
  }

  busy(button, on) {
    if (button) button.disabled = on;
  }

  error(block, message) {
    const el = this.role(block, "error");
    if (el) { el.textContent = message; el.classList.add("is-visible"); }
  }

  clearError(block) {
    const el = this.role(block, "error");
    if (el) { el.textContent = ""; el.classList.remove("is-visible"); }
  }

  hideOk(block) {
    const el = this.role(block, "ok");
    if (el) el.hidden = true;
  }
}
